import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../components/hooks";
import { markOnboarded } from "../../store/slices/onboardingSlice";
import { useStyles } from "./Onboarding.style";
import clsx from "classnames";
import arrowRight from "./assets/arrow-right.svg";
import { StaticImage } from "../../components/common/StaticImage";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyVariant } from "../../constants/typography";
import Button from "../../components/core/Button/Button";
import { SliderContainer } from "../../components/common/SliderContainer";
import { useNavigate } from "react-router-dom";
import { PageRoutes } from "../../constants/historyGaming";
import { t } from "i18next";
import { assetsImages } from "../../models/theme";
import { login } from "@onmo/auth";
import { FEATURES } from "../../types/theme";
import { HomepageMd } from "../../models/homepage";
import { getSlides } from "../../models/onboarding";

const slides = getSlides();

const Onboarding: React.FC = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { theme } = useAppSelector((state) => state.theme);
  const hideGuestPage =
    theme?.hideFeature?.includes(FEATURES.guestPage) || false;

  const handleFinish = async () => {
    dispatch(markOnboarded());
    if (!hideGuestPage) {
      navigate(PageRoutes.Guest);
      return;
    }
    await login(window.location.origin);
  };

  const handleNext = async () => {
    if (currentIndex < slides.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      await handleFinish();
    }
  };

  const { title, description } = slides[currentIndex];
  const localizedTitle = HomepageMd.getLocalized(title) ?? "";
  const localizedDescription = HomepageMd.getLocalized(description) ?? "";
  const buttontext =
    currentIndex < slides.length - 1 ? t("next") : t("common_Close");

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <img
          src={assetsImages.headerLogo}
          alt="Vodacom Logo"
          className={classes.logo}
        />
        <Button
          variant="borderless"
          color="primary"
          onClick={handleFinish}
          className={classes.skipTxt}
        >
          {t("skip")}
        </Button>
      </div>

      <div className={classes.space32} />

      {/* Slider */}
      <div className={classes.slider}>
        <SliderContainer
          nbSlide={slides.length}
          dotsClass={classes.paginationDots}
          activeSlide={currentIndex}
          settings={{
            autoplay: false,
            infinite: false,
            arrows: false,
            variableWidth: false,
            adaptiveHeight: true,
            responsive: [
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
              {
                breakpoint: 600,
                settings: {
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              },
            ],
          }}
          afterChangeSlider={(next) => setCurrentIndex(next)}
        >
          {slides.map((slide, idx) => {
            const image =
              typeof slide.src === "string"
                ? { "1x": slide.src, "2x": slide.src, "3x": slide.src }
                : slide.src;

            return (
              <div className={classes.slide} key={idx}>
                <div className={classes.imageCtr}>
                  <StaticImage
                    src={image["1x"]}
                    src2x={image["2x"]}
                    src3x={image["3x"]}
                    className={classes.image}
                    alt={HomepageMd.getLocalized(slide.title) ?? ""}
                  />
                </div>
              </div>
            );
          })}
        </SliderContainer>
      </div>

      {/* Dots */}
      {slides.length > 1 && (
        <div className={classes.paginationDots}>
          {slides.map((_, idx) => (
            <span
              key={idx}
              className={clsx({ active: idx === currentIndex })}
            />
          ))}
        </div>
      )}

      {/* Text */}
      <div className={classes.textWrapper}>
        <Typography variant={TypographyVariant.HeadingH4Bold}>
          {localizedTitle}
        </Typography>
        <Typography
          variant={TypographyVariant.BodyB5Regular}
          className={classes.marginDes}
        >
          {localizedDescription}
        </Typography>
      </div>

      {/* Button */}
      <div className={classes.buttonWrapper}>
        <Button variant="contained" size="large" fullWidth onClick={handleNext}>
          {buttontext}
          <img src={arrowRight} alt="Next" className={classes.buttonImg} />
        </Button>
      </div>
    </div>
  );
};

export default Onboarding;
